import { Button, CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Subscription } from "rxjs";
import { BetfairHistoricEvent } from "../../types/betfair/price-matching/betfair-historic-event";
import {
  PriceMatchingData,
  PriceMatchingWebsocketUpdate,
  WebsocketStatus,
} from "../../types/betfair/price-matching/price-matching-data";
import { BetfairRoute, DeveloperRoute } from "../../types/route-helpers";
import { services } from "../../types/services";
import PriceMatchingDataTable from "./price-matching-data-table";
import PriceMatchingFilters from "./price-matching-filters";
import PriceMatchingRunsLineChart from "./price-matching-runs-line-chart";

export default function PriceMatchingPage(): React.JSX.Element {
  const [loading, setLoading] = useState<boolean>(false);
  const [recalculatingPrices, setRecalculatingPrices] =
    useState<boolean>(false);
  const [events, setEvents] = useState<
    Map<number, Map<string, Map<number, BetfairHistoricEvent[]>>>
  >(new Map());
  const [selectedEvent, setSelectedEvent] = useState<string>("");
  const [eventData, setEventData] = useState<PriceMatchingData>(null);
  const [latestWebsocketUpdate, setLatestWebsocketUpdate] =
    useState<PriceMatchingWebsocketUpdate>(null);

  useEffect(() => {
    const subscriptions: Subscription[] = [
      services.priceMatchingService.latestWebsocketUpdate.subscribe(
        (update) => {
          if (update.status === WebsocketStatus.COMPLETE) {
            setRecalculatingPrices(false);
            setLatestWebsocketUpdate(null);
            return;
          }
          setLatestWebsocketUpdate(update);
        }
      ),
    ];

    services.priceMatchingService.getPriceMatchingEvents().then(setEvents);

    return () =>
      subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, []);

  const handleEventChange = (event: string) => {
    setSelectedEvent(event);
    if (event !== "") {
      setLoading(true);
      services.priceMatchingService
        .getPriceMatchingDataForEvent(event)
        .then((response) => {
          setLoading(false);
          setEventData(response);
        });
    }
  };

  const updateHistoricData = () => {
    setLoading(true);
    services.priceMatchingService
      .updateBetfairHistoricEvents()
      .finally(() => setLoading(false));
  };

  const updateHistoricPrices = () => {
    setLoading(true);
    services.priceMatchingService
      .updateBetfairHistoricPrices()
      .then(() => setRecalculatingPrices(true))
      .finally(() => setLoading(false));
  };

  const controlsDisabled = loading || recalculatingPrices;

  return (
    <BetfairRoute>
      <div className="price-matching-page">
        <DeveloperRoute>
          <Button
            onClick={updateHistoricData}
            variant="contained"
            disabled={controlsDisabled}
          >
            Update Historic Events
          </Button>
          <Button
            onClick={updateHistoricPrices}
            variant="contained"
            disabled={controlsDisabled}
          >
            Update Historic Prices
          </Button>
        </DeveloperRoute>

        {latestWebsocketUpdate && (
          <div>
            {`Recalculating event ${latestWebsocketUpdate.amountDone} of
            ${latestWebsocketUpdate.totalSize}: ${latestWebsocketUpdate.event}`}
          </div>
        )}

        <PriceMatchingFilters
          onEventSelected={handleEventChange}
          selectedEvent={selectedEvent}
          events={events}
          disabled={controlsDisabled}
        />

        {controlsDisabled && <CircularProgress />}

        {!controlsDisabled && eventData && (
          <div style={{ overflowX: "auto" }}>
            <div>Cricinfo number: {eventData.eventInfo.match.espnNum}</div>
            <PriceMatchingRunsLineChart eventData={eventData} />
            <PriceMatchingDataTable eventData={eventData} />
          </div>
        )}
      </div>
    </BetfairRoute>
  );
}
